import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import { interval } from "rxjs";

export type BulkEditLoadingPhase = "validating" | "updating" | "finalizing";
export type BulkEditProgressMode = "save" | "undo" | "delete";

@UntilDestroy()
@Component({
	selector: "tg-bulk-edit-progress-overlay",
	templateUrl: "./bulk-edit-progress-overlay.component.html",
	styleUrls: ["./bulk-edit-progress-overlay.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkEditProgressOverlayComponent implements OnInit, OnDestroy {
	@Input() percent = 0;
	@Input() phase: BulkEditLoadingPhase = "validating";
	@Input() mode: BulkEditProgressMode = "save";
	@Input() failed = 0;

	visibleDotCount = 0;

	constructor(
		private translate: TranslateService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		interval(400)
			.pipe(untilDestroyed(this))
			.subscribe((tick) => {
				this.visibleDotCount = tick % 4;
				this.cdr.markForCheck();
			});
	}

	ngOnDestroy(): void {}

	get dotsText(): string {
		return ".".repeat(this.visibleDotCount);
	}

	get phaseLabel(): string {
		const prefix =
			this.mode === "undo"
				? "time.edit-undo-phase"
				: this.mode === "delete"
					? "time.bulk-delete-phase"
					: "time.bulk-edit-phase";
		const suffix =
			this.phase === "validating" ? "validating" : this.phase === "finalizing" ? "finalizing" : "updating";
		return this.translate.instant(`${prefix}-${suffix}`);
	}

	get displayPercent(): number {
		return Math.min(100, Math.max(0, Math.round(this.percent)));
	}

	get failedLabelKey(): string {
		switch (this.mode) {
			case "delete":
				return "time.bulk-progress-failed-delete";
			case "undo":
				return "time.bulk-progress-failed-undo";
			default:
				return "time.bulk-progress-failed-save";
		}
	}
}
